import React from "react";
import {
  Box,
  Container,
  Typography,
  Link as MuiLink,
  List,
  ListItem,
} from "@mui/material";
import { Link } from "react-router-dom";
import { PageTransition } from "../components/common/PageTransition";
import { SEOHead } from "../components/seo/SEOHead";
import { usePublicGalleries } from "../hooks/useGalleries";
import { getFullUrl } from "../utils/seo";
import { CircularProgress } from "@mui/material";
import { trackFileDownload } from "../utils/analytics";

export const SitemapPage: React.FC = () => {
  const { data: galleries, isLoading } = usePublicGalleries();

  const sitemapDescription =
    "Complete sitemap of Mariah Blue Photography portfolio. Browse all galleries, pages, and photography collections.";

  return (
    <PageTransition>
      <SEOHead
        title="Sitemap"
        description={sitemapDescription}
        url={getFullUrl("/sitemap")}
      />
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          pt: { xs: "80px", md: "80px" },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ py: { xs: 4, md: 6 } }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontFamily: '"Cinzel Decorative", serif',
                fontSize: { xs: "2rem", md: "2.75rem" },
                fontWeight: 400,
                mb: 2,
                textAlign: "center",
              }}
            >
              Sitemap
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                mb: 4,
                color: "text.secondary",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              {sitemapDescription}
            </Typography>

            {/* Main Pages */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontFamily: '"Cinzel Decorative", serif',
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Main Pages
              </Typography>
              <List>
                <ListItem sx={{ pl: 0 }}>
                  <MuiLink
                    component={Link}
                    to="/"
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Home
                  </MuiLink>
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <MuiLink
                    component={Link}
                    to="/galleries"
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Galleries
                  </MuiLink>
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <MuiLink
                    component={Link}
                    to="/about"
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    About
                  </MuiLink>
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <MuiLink
                    component={Link}
                    to="/contact"
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Contact
                  </MuiLink>
                </ListItem>
              </List>
            </Box>

            {/* Galleries */}
            <Box>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontFamily: '"Cinzel Decorative", serif',
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Photography Galleries
              </Typography>
              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : galleries && galleries.length > 0 ? (
                <List>
                  {galleries.map((gallery) => (
                    <ListItem key={gallery.id} sx={{ pl: 0, mb: 0.5 }}>
                      <MuiLink
                        component={Link}
                        to={`/gallery/${gallery.id}`}
                        sx={{
                          textDecoration: "none",
                          color: "primary.main",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {gallery.title}
                        {gallery.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              ml: 1,
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            - {gallery.description}
                          </Typography>
                        )}
                      </MuiLink>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No galleries available yet.
                </Typography>
              )}
            </Box>

            {/* XML Sitemaps */}
            <Box
              sx={{
                mt: 4,
                pt: 4,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontFamily: '"Cinzel Decorative", serif',
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                XML Sitemaps
              </Typography>
              <List>
                <ListItem sx={{ pl: 0 }}>
                  <MuiLink
                    href="/api/sitemap.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackFileDownload("sitemap.xml", "xml")}
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Sitemap (XML)
                  </MuiLink>
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <MuiLink
                    href="/api/sitemap-images.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackFileDownload("sitemap-images.xml", "xml")
                    }
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Image Sitemap (XML)
                  </MuiLink>
                </ListItem>
              </List>
            </Box>
          </Box>
        </Container>
      </Box>
    </PageTransition>
  );
};
